/**
 * @file Row grid for ONE table slice of a mapping review: a client-side
 * paginated AntD Table (~100 rows/page) keyed by global row index, reused
 * across the entity-import and document surfaces (mutators keyed by group id).
 * Each row carries a keyboard Checkbox (shift-range), the Typesafe class chip +
 * shared band-coloured confidence Tag, one single-line cell per source column,
 * and a skip/restore toggle. Edited cells show an "edited" marker + provenance
 * tooltip, cleaned cells a "cleaned" marker; editable cells are text buttons
 * (Enter/Space/F2 edit, Esc cancel); reference cells show value + one
 * resolution chip into the Drawer. Low-confidence rows are tinted (rejected and
 * chunk tints win), a "Needs review" toggle filters the risky/unresolved rows,
 * the class column sorts by confidence ascending, decision columns are frozen,
 * and every editable control is disabled while the approval is saving. Rows
 * beyond the import cap (`baseRows.length`) are shown for context but never
 * decidable.
 */
import { FC, useContext, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Input, Table, Tag, Tooltip, Typography, theme } from 'antd';
import { EditOutlined, LinkOutlined, StopOutlined, UndoOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useSnapshot } from 'valtio';
import { coerceCellValue, tableReferenceExtractedKey } from '#types';
import type { RowClassificationTag } from '#types';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import FlexBox from '../../atoms/FlexBox';
import FlexCol from '../../atoms/FlexCol';
import {
  clearSelection,
  selectRowsWhere,
  setCellOverride,
  toggleRowRejected,
  toggleRowSelected,
} from './mappingReviewSession';
import type { MappingReviewTableSlice } from './mappingReviewState';
import { applyCellCleaningRules } from './applyCellCleaningRules';
import { formatCoercedValue } from './formatCoercedValue';
import { mappingReviewConfidence } from './mappingReviewConfidence';
import { mappingReviewLabels } from './mappingReviewLabels';
import MappingReviewConfidenceTag from './MappingReviewConfidenceTag';
import { useMappingReviewSubmitting } from './useMappingReviewSubmitting';
import type { TargetFieldOption } from '@/services/editorExtractors';
import type { ReferenceValuesTarget } from './MappingReviewColumns';

const { Text, Title } = Typography;

const PAGE_SIZE = 100;

type RowItem =
  | { kind: 'row'; index: number; row: Record<string, string | number | null> }
  | { kind: 'chunk'; chunkId: string; description: string };

interface MappingReviewRowsProps {
  slice: MappingReviewTableSlice;
  targetFields: TargetFieldOption[];
  tagFilter: RowClassificationTag | null;
  onOpenReferenceValues: (target: ReferenceValuesTarget) => void;
}

const MappingReviewRows: FC<MappingReviewRowsProps> = ({
  slice,
  targetFields,
  tagFilter,
  onOpenReferenceValues,
}) => {
  const { token } = theme.useToken();
  const { fieldTypes } = useContext(FieldTypeContext);
  const snap = useSnapshot(slice);
  const submitting = useMappingReviewSubmitting();
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<{ index: number; sourceColumn: string } | null>(null);
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
  const cancelEditRef = useRef(false);

  const chunks = snap.chunks;
  const classifications = snap.baseRows;
  const coverage = classifications.length;
  const beyondCapCount = chunks.reduce((n, c) => n + c.rows.length, 0) - coverage;
  const capMessage = `Beyond the import cap (${coverage} rows) — this row will not be written`;
  const activeChunkId = snap.activeChunkId;
  const allChunks = activeChunkId === null;
  const referenceTargets = useMemo(
    () => new Set(targetFields.filter((f) => f.reference).map((f) => f.id)),
    [targetFields],
  );

  const { items, visibleIndexes } = useMemo(() => {
    const listed: RowItem[] = [];
    const visible: number[] = [];
    chunks.reduce((offset, chunk) => {
      const inScope = allChunks || chunk.label.id === activeChunkId;
      if (inScope) {
        const chunkItems: RowItem[] = [];
        chunk.rows.forEach((row, i) => {
          const index = offset + i;
          if (tagFilter && classifications[index]?.tag !== tagFilter) return;
          chunkItems.push({ kind: 'row', index, row });
          visible.push(index);
        });
        if (chunkItems.length > 0) {
          if (allChunks && chunks.length > 1) {
            listed.push({ kind: 'chunk', chunkId: chunk.label.id, description: chunk.label.description });
          }
          listed.push(...chunkItems);
        }
      }
      return offset + chunk.rows.length;
    }, 0);
    return { items: listed, visibleIndexes: visible };
  }, [chunks, classifications, allChunks, activeChunkId, tagFilter]);

  const needsReviewIndexes = useMemo(() => {
    const refColumns = snap.columns.filter(
      (c) => referenceTargets.has(c.targetField) || c.resolution,
    );
    const flagged = new Set<number>();
    items.forEach((item) => {
      if (item.kind !== 'row') return;
      const classification = classifications[item.index];
      if (classification && mappingReviewConfidence(classification.confidence).warn) {
        flagged.add(item.index);
        return;
      }
      const unresolved = refColumns.some((column) => {
        const override = snap.cellOverrides[item.index]?.[column.sourceColumn];
        const raw = override !== undefined ? override : item.row[column.sourceColumn];
        const key = tableReferenceExtractedKey(raw);
        if (!key) return false;
        const resolution = column.resolution?.[key];
        return resolution?.kind !== 'entity' && resolution?.kind !== 'manual';
      });
      if (unresolved) flagged.add(item.index);
    });
    return flagged;
  }, [items, classifications, snap.columns, snap.cellOverrides, referenceTargets]);

  const needsReviewCount = needsReviewIndexes.size;
  const needsReviewDisabled = needsReviewCount === 0;
  const hasChunkHeaders = items.some((it) => it.kind === 'chunk');
  const needsReviewActive = needsReviewOnly && !needsReviewDisabled;
  const dataSource = needsReviewActive
    ? items.filter((it) => it.kind === 'row' && needsReviewIndexes.has(it.index))
    : items;
  const currentPage = Math.min(page, Math.max(1, Math.ceil(dataSource.length / PAGE_SIZE)));

  const selectedVisible = visibleIndexes.filter((i) => snap.selection.selected[i]).length;
  const allVisibleSelected = visibleIndexes.length > 0 && selectedVisible === visibleIndexes.length;

  const openEditor = (index: number, sourceColumn: string) => {
    if (submitting) return;
    cancelEditRef.current = false;
    setEditing({ index, sourceColumn });
  };

  const sortConfidence = (item: RowItem) =>
    item.kind === 'row'
      ? classifications[item.index]?.confidence ?? Number.POSITIVE_INFINITY
      : Number.POSITIVE_INFINITY;

  const headerCheckbox = (
    <Checkbox
      aria-label="Select all visible rows"
      checked={allVisibleSelected}
      disabled={submitting}
      indeterminate={selectedVisible > 0 && !allVisibleSelected}
      onChange={(e) => {
        if (e.target.checked) {
          const visibleSet = new Set(visibleIndexes);
          selectRowsWhere(slice.groupId, (i) => visibleSet.has(i));
        } else {
          clearSelection(slice.groupId);
        }
      }}
    />
  );

  const chunkHeaderCell = (item: RowItem) =>
    item.kind === 'chunk' ? { colSpan: 4 + snap.columns.length } : {};

  const renderCell = (item: RowItem, sourceColumn: string) => {
    if (item.kind === 'chunk') return null;
    const rejected = Boolean(snap.rejectedRows[item.index]);
    const beyondCap = item.index >= coverage;
    const editable = !rejected && !beyondCap;
    const override = snap.cellOverrides[item.index]?.[sourceColumn];
    const edited = override !== undefined;
    const original = item.row[sourceColumn];
    const raw = edited ? override : original;
    const column = snap.columns.find((c) => c.sourceColumn === sourceColumn);
    const reference = Boolean(column && (referenceTargets.has(column.targetField) || column.resolution));

    if (editing && editing.index === item.index && editing.sourceColumn === sourceColumn) {
      return (
        <Input
          size="small"
          autoFocus
          defaultValue={raw == null ? '' : String(raw)}
          aria-label={`Edit cell ${sourceColumn} in row ${item.index + 1}`}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              cancelEditRef.current = true;
              setEditing(null);
            }
          }}
          onBlur={(e) => {
            if (cancelEditRef.current) {
              cancelEditRef.current = false;
              return;
            }
            setCellOverride(slice.groupId, item.index, sourceColumn, e.target.value === '' ? null : e.target.value);
            setEditing(null);
          }}
          onPressEnter={(e) => {
            const value = (e.target as HTMLInputElement).value;
            setCellOverride(slice.groupId, item.index, sourceColumn, value === '' ? null : value);
            setEditing(null);
          }}
        />
      );
    }

    if (column && reference) {
      const key = tableReferenceExtractedKey(raw);
      const resolution = key ? column.resolution?.[key] : undefined;
      const chip = !key ? null : resolution?.kind === 'entity' ? (
        <Tag icon={<LinkOutlined />} style={{ margin: 0, cursor: 'pointer' }}>
          {resolution.cacheLabel ?? resolution.docId}
        </Tag>
      ) : resolution?.kind === 'manual' ? (
        <Tag style={{ margin: 0, cursor: 'pointer' }}>
          {mappingReviewLabels.plainText} · {resolution.text}
        </Tag>
      ) : (
        <Tag color="warning" style={{ margin: 0, cursor: 'pointer' }}>
          {mappingReviewLabels.noMatch}
        </Tag>
      );
      return (
        <FlexCol noGap noGrow alignStart>
          <Tooltip title={beyondCap ? capMessage : undefined}>
            <Text delete={rejected} type={rejected ? 'secondary' : undefined}>
              {raw == null ? '' : String(raw)}
            </Text>
          </Tooltip>
          {chip && (
            <Button
              type="text"
              size="small"
              style={{ padding: 0, height: 'auto' }}
              aria-label={`Open resolution for ${sourceColumn} value in row ${item.index + 1}`}
              onClick={() =>
                onOpenReferenceValues({
                  groupId: slice.groupId,
                  sourceColumn,
                  filter: resolution ? 'all' : 'unresolved',
                })
              }
            >
              {chip}
            </Button>
          )}
        </FlexCol>
      );
    }

    const cleaned = column
      ? applyCellCleaningRules(raw, column.rules, snap.locale, snap.dateLocale, snap.centuryPivot, snap.dateZone)
      : raw;
    const targetFieldType = column ? fieldTypes[column.targetField] : undefined;
    const fieldDataTypeId = targetFieldType?.fieldDataTypeId;
    const coerced = coerceCellValue(
      cleaned,
      fieldDataTypeId,
      {
        numberLocale: snap.locale,
        dateLocale: snap.dateLocale,
        centuryPivot: snap.centuryPivot,
        dateZone: snap.dateZone,
      },
      {
        ...(targetFieldType?.props?.options ? { options: targetFieldType.props.options } : {}),
        ...(column?.percentScale ? { percentScale: column.percentScale } : {}),
      },
    );
    const wontImport =
      coerced.value === null && cleaned != null && String(cleaned).trim() !== '';
    const changed = cleaned !== raw && raw != null;
    const display = cleaned == null ? '' : String(cleaned);
    const coercedText = formatCoercedValue(coerced.value);
    const coercedPreview =
      !wontImport && coerced.value !== null && coercedText !== display ? (
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          → {coercedText}
        </Text>
      ) : null;
    const tooltipTitle = beyondCap
      ? capMessage
      : wontImport
        ? `Won't import — "${String(cleaned)}" isn't a valid value for this field type and lands blank`
        : edited
          ? `Edited by you — was: "${original == null ? '' : String(original)}"`
          : changed
            ? `Extracted: "${String(raw)}" → Cleaned: "${String(cleaned)}"`
            : undefined;
    const marker = wontImport ? (
      <Text type="warning" style={{ fontSize: token.fontSizeSM }}>
        won&apos;t import
      </Text>
    ) : edited ? (
      <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
        edited
      </Text>
    ) : changed ? (
      <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
        cleaned
      </Text>
    ) : null;
    const content = (
      <FlexBox noGrow gap={5} justifyStart>
        {wontImport ? (
          <Text delete={rejected} style={{ color: token.colorTextQuaternary }}>
            —
          </Text>
        ) : (
          <Text delete={rejected} type={rejected ? 'secondary' : undefined}>
            {display}
          </Text>
        )}
        {coercedPreview}
        {marker}
      </FlexBox>
    );

    if (!editable) {
      return <Tooltip title={tooltipTitle}>{content}</Tooltip>;
    }
    return (
      <Tooltip title={tooltipTitle}>
        <Button
          type="text"
          size="small"
          disabled={submitting}
          aria-label={`Edit cell ${sourceColumn} in row ${item.index + 1}`}
          style={{ padding: '0 5px', height: 'auto', width: '100%', justifyContent: 'flex-start' }}
          onClick={() => openEditor(item.index, sourceColumn)}
          onKeyDown={(e) => {
            if (e.key === 'F2') {
              e.preventDefault();
              openEditor(item.index, sourceColumn);
            }
          }}
        >
          {content}
          <EditOutlined style={{ color: token.colorTextQuaternary, fontSize: token.fontSizeSM }} />
        </Button>
      </Tooltip>
    );
  };

  const columns: ColumnsType<RowItem> = [
    {
      key: 'select',
      title: headerCheckbox,
      width: 32,
      fixed: 'left',
      onCell: (item) => (item.kind === 'chunk' ? { colSpan: 0 } : {}),
      render: (_, item) =>
        item.kind === 'row' ? (
          <Tooltip title={item.index >= coverage ? capMessage : undefined}>
            <Checkbox
              aria-label={`Select row ${item.index + 1}`}
              checked={Boolean(snap.selection.selected[item.index])}
              disabled={item.index >= coverage || submitting}
              onChange={(e) =>
                toggleRowSelected(
                  slice.groupId,
                  item.index,
                  (e.nativeEvent as MouseEvent).shiftKey,
                )
              }
            />
          </Tooltip>
        ) : null,
    },
    {
      key: 'index',
      title: '#',
      width: 50,
      fixed: 'left',
      onCell: (item) => chunkHeaderCell(item),
      render: (_, item) =>
        item.kind === 'chunk' ? (
          <Text strong type="secondary">
            ── {item.description} ──
          </Text>
        ) : (
          <Text type="secondary">{item.index + 1}</Text>
        ),
    },
    {
      key: 'tag',
      title: 'Tag',
      width: 200,
      fixed: 'left',
      ...(hasChunkHeaders
        ? {}
        : {
            defaultSortOrder: 'ascend' as const,
            sorter: (a: RowItem, b: RowItem) => sortConfidence(a) - sortConfidence(b),
          }),
      onCell: (item) => (item.kind === 'chunk' ? { colSpan: 0 } : {}),
      render: (_, item) => {
        if (item.kind === 'chunk') return null;
        const classification = classifications[item.index];
        if (!classification) {
          return item.index >= coverage ? (
            <Tooltip title={capMessage}>
              <Tag style={{ margin: 0 }}>beyond cap</Tag>
            </Tooltip>
          ) : null;
        }
        return (
          <FlexBox noGrow gap={5} justifyStart>
            <Tooltip
              title={`Typesafe classified this row as "${mappingReviewLabels.rowClass[classification.tag]}"`}
            >
              <Tag color={mappingReviewLabels.tagColor[classification.tag]} style={{ margin: 0 }}>
                {mappingReviewLabels.rowClass[classification.tag]}
              </Tag>
            </Tooltip>
            <MappingReviewConfidenceTag manual={false} confidence={classification.confidence} />
            {snap.rejectedRows[item.index] && <Tag style={{ margin: 0 }}>rejected</Tag>}
          </FlexBox>
        );
      },
    },
    ...snap.columns.map((column) => ({
      key: `col-${column.sourceColumn}`,
      title: column.sourceColumn,
      onCell: (item: RowItem) => (item.kind === 'chunk' ? { colSpan: 0 } : {}),
      render: (_: unknown, item: RowItem) => renderCell(item, column.sourceColumn),
    })),
    {
      key: 'actions',
      title: '',
      width: 40,
      fixed: 'right',
      onCell: (item) => (item.kind === 'chunk' ? { colSpan: 0 } : {}),
      render: (_, item) => {
        if (item.kind === 'chunk') return null;
        const rejected = Boolean(snap.rejectedRows[item.index]);
        const beyondCap = item.index >= coverage;
        return (
          <Tooltip title={beyondCap ? capMessage : undefined}>
            <Button
              type="text"
              size="small"
              icon={rejected ? <UndoOutlined /> : <StopOutlined />}
              disabled={beyondCap || submitting}
              aria-label={
                rejected
                  ? `${mappingReviewLabels.restore} row ${item.index + 1}`
                  : `${mappingReviewLabels.skip} row ${item.index + 1}`
              }
              onClick={() => toggleRowRejected(slice.groupId, item.index)}
            />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <FlexCol noGrow gap={5} stretch>
      <FlexBox noGrow justifyStart gap={10}>
        <Title level={5} style={{ margin: 0 }}>
          Rows ({coverage})
        </Title>
        <Tag.CheckableTag
          checked={needsReviewActive}
          disabled={needsReviewDisabled}
          aria-label="Show only rows needing review"
          onChange={setNeedsReviewOnly}
        >
          Needs review ({needsReviewCount})
        </Tag.CheckableTag>
      </FlexBox>
      <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
        Each extracted row that will be written — edit a cell, or skip rows you don&apos;t want imported.
      </Text>
      {beyondCapCount > 0 && (
        <Text type="warning" style={{ fontSize: token.fontSizeSM }}>
          {beyondCapCount} row{beyondCapCount !== 1 ? 's' : ''} beyond the import cap of {coverage}{' '}
          are shown for context but will not be written.
        </Text>
      )}
      <Table<RowItem>
        size="small"
        aria-label="Mapping review rows"
        dataSource={dataSource}
        columns={columns}
        rowKey={(item) => (item.kind === 'row' ? `r${item.index}` : `c${item.chunkId}`)}
        pagination={{
          current: currentPage,
          pageSize: PAGE_SIZE,
          onChange: setPage,
          showSizeChanger: false,
          size: 'small',
        }}
        style={{
          width: '100%',
        }}
        scroll={{ x: 'max-content' }}
        onRow={(item) => {
          const rejectedOrChunk =
            item.kind === 'chunk' || (item.kind === 'row' && Boolean(snap.rejectedRows[item.index]));
          const warn =
            item.kind === 'row' &&
            Boolean(
              classifications[item.index] &&
                mappingReviewConfidence(classifications[item.index].confidence).warn,
            );
          return {
            style: rejectedOrChunk
              ? { background: token.colorFillQuaternary }
              : warn
                ? { background: token.colorWarningBg }
                : {},
            'aria-selected':
              item.kind === 'row' ? Boolean(snap.selection.selected[item.index]) : undefined,
          };
        }}
      />
    </FlexCol>
  );
};

export default MappingReviewRows;
